/**
 * PromptDiffViewer — Story 8.2.
 *
 * Renders the structured diff returned by `POST /admin/prompts/:id/diff`:
 *   - System prompt: per-section side-by-side OR unified view.
 *   - Few-shot examples: added / removed / modified, indexed.
 *   - JSON schema: before/after when changed, else "unchanged".
 *
 * This is mounted inside a Dialog by PromptDetail.tsx. The viewer itself
 * doesn't fetch — the caller passes `open + versionAId + versionBId` and we
 * run the POST once the dialog opens. When `open=false` the query is disabled.
 *
 * Rendering choices:
 *   - No diff library (keeps admin bundle lean). Side-by-side uses simple
 *     line-index alignment against addedLines/removedLines emitted by the
 *     backend. For each changed section we show the raw before + after
 *     blocks with `+`/`-` prefixes on changed lines.
 *   - `<details>` for collapsible sections (native, no Radix dep).
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, apiClient } from '@/lib/api';
import type { PromptDiffResponse, SectionChangeType, SectionDiff } from '@/lib/prompts-types';

export interface PromptDiffViewerProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  promptId: string;
  versionAId: string;
  versionBId: string;
  versionANumber?: number;
  versionBNumber?: number;
}

type ViewMode = 'split' | 'unified';

function changeBadge(kind: SectionChangeType): string {
  switch (kind) {
    case 'added':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
    case 'removed':
      return 'bg-red-500/10 text-red-700 dark:text-red-300';
    case 'modified':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-300';
    case 'unchanged':
      return 'bg-muted text-muted-foreground';
  }
}

function changeLabel(kind: SectionChangeType): string {
  switch (kind) {
    case 'added':
      return 'novo';
    case 'removed':
      return 'removido';
    case 'modified':
      return 'modificado';
    case 'unchanged':
      return 'sem mudança';
  }
}

function UnifiedBlock({ section }: { section: SectionDiff }) {
  const beforeLines = (section.before ?? '').split('\n');
  const afterLines = (section.after ?? '').split('\n');
  const addedSet = new Set(section.addedLines);
  const removedSet = new Set(section.removedLines);

  // Walk after lines, marking any present in addedSet with `+`; also emit
  // removed lines that belonged to before but not after. We emit removed
  // lines before their nearest preserved-line neighbor for readability.
  const chunks: Array<{ kind: '+' | '-' | ' '; text: string }> = [];
  for (const l of beforeLines) {
    if (!afterLines.includes(l) && removedSet.has(l)) {
      chunks.push({ kind: '-', text: l });
    }
  }
  for (const l of afterLines) {
    if (addedSet.has(l)) {
      chunks.push({ kind: '+', text: l });
    } else {
      chunks.push({ kind: ' ', text: l });
    }
  }

  return (
    <pre className="overflow-x-auto rounded bg-muted/30 p-3 text-xs leading-relaxed">
      {chunks.map((c, idx) => (
        <div
          key={idx}
          className={
            c.kind === '+'
              ? 'text-emerald-600 dark:text-emerald-400'
              : c.kind === '-'
                ? 'text-red-600 dark:text-red-400'
                : 'text-muted-foreground'
          }
        >
          <span className="select-none pr-2">{c.kind}</span>
          {c.text}
        </div>
      ))}
    </pre>
  );
}

function SplitBlock({ section }: { section: SectionDiff }) {
  const addedSet = new Set(section.addedLines);
  const removedSet = new Set(section.removedLines);
  const beforeLines = (section.before ?? '').split('\n');
  const afterLines = (section.after ?? '').split('\n');

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <pre className="overflow-x-auto rounded bg-muted/30 p-3 text-xs leading-relaxed">
        {beforeLines.map((l, idx) => (
          <div
            key={idx}
            className={
              removedSet.has(l)
                ? 'bg-red-500/15 text-red-700 dark:text-red-300'
                : 'text-muted-foreground'
            }
          >
            {l || ' '}
          </div>
        ))}
      </pre>
      <pre className="overflow-x-auto rounded bg-muted/30 p-3 text-xs leading-relaxed">
        {afterLines.map((l, idx) => (
          <div
            key={idx}
            className={
              addedSet.has(l)
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                : 'text-muted-foreground'
            }
          >
            {l || ' '}
          </div>
        ))}
      </pre>
    </div>
  );
}

function SectionItem({ section, mode }: { section: SectionDiff; mode: ViewMode }) {
  const label = section.name === '__header__' ? '(preâmbulo)' : section.name;
  return (
    <details className="rounded border border-border" open={section.changeType !== 'unchanged'}>
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm">
        <span className="font-medium">{label}</span>
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${changeBadge(section.changeType)}`}
        >
          {changeLabel(section.changeType)}
        </span>
      </summary>
      <div className="border-t border-border p-3">
        {section.changeType === 'unchanged' ? (
          <p className="text-xs text-muted-foreground">Sem mudanças nesta seção.</p>
        ) : mode === 'split' ? (
          <SplitBlock section={section} />
        ) : (
          <UnifiedBlock section={section} />
        )}
      </div>
    </details>
  );
}

function FewShotBlock({ diff }: { diff: PromptDiffResponse['fewShots'] }) {
  const { added, removed, modified } = diff;
  if (!added.length && !removed.length && !modified.length) {
    return <p className="text-xs text-muted-foreground">Sem mudanças nos few-shots.</p>;
  }
  return (
    <div className="space-y-3 text-xs">
      {added.length > 0 && (
        <div>
          <p className="font-medium text-emerald-600 dark:text-emerald-400">Adicionados</p>
          <ul className="mt-1 space-y-1">
            {added.map((item) => (
              <li key={`a-${item.index}`} className="rounded bg-muted/30 p-2">
                <span className="text-muted-foreground">#{item.index}</span>
                <pre className="mt-1 overflow-x-auto">{JSON.stringify(item.value, null, 2)}</pre>
              </li>
            ))}
          </ul>
        </div>
      )}
      {removed.length > 0 && (
        <div>
          <p className="font-medium text-red-600 dark:text-red-400">Removidos</p>
          <ul className="mt-1 space-y-1">
            {removed.map((item) => (
              <li key={`r-${item.index}`} className="rounded bg-muted/30 p-2">
                <span className="text-muted-foreground">#{item.index}</span>
                <pre className="mt-1 overflow-x-auto">{JSON.stringify(item.value, null, 2)}</pre>
              </li>
            ))}
          </ul>
        </div>
      )}
      {modified.length > 0 && (
        <div>
          <p className="font-medium text-amber-600 dark:text-amber-400">Modificados</p>
          <ul className="mt-1 space-y-1">
            {modified.map((item) => (
              <li key={`m-${item.index}`} className="rounded bg-muted/30 p-2">
                <span className="text-muted-foreground">#{item.index}</span>
                <div className="mt-1 grid grid-cols-1 gap-2 md:grid-cols-2">
                  <pre className="overflow-x-auto rounded bg-red-500/10 p-2 text-red-700 dark:text-red-300">
                    {JSON.stringify(item.beforeValue, null, 2)}
                  </pre>
                  <pre className="overflow-x-auto rounded bg-emerald-500/10 p-2 text-emerald-700 dark:text-emerald-300">
                    {JSON.stringify(item.afterValue, null, 2)}
                  </pre>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function JsonSchemaBlock({ diff }: { diff: PromptDiffResponse['jsonSchema'] }) {
  if (!diff.changed) {
    return <p className="text-xs text-muted-foreground">JSON schema sem mudanças.</p>;
  }
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      <pre className="overflow-x-auto rounded bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300">
        {JSON.stringify(diff.before, null, 2)}
      </pre>
      <pre className="overflow-x-auto rounded bg-emerald-500/10 p-2 text-xs text-emerald-700 dark:text-emerald-300">
        {JSON.stringify(diff.after, null, 2)}
      </pre>
    </div>
  );
}

export function PromptDiffViewer({
  open,
  onOpenChange,
  promptId,
  versionAId,
  versionBId,
  versionANumber,
  versionBNumber,
}: PromptDiffViewerProps) {
  const [mode, setMode] = useState<ViewMode>('split');

  const { data, isLoading, isError, error, refetch } = useQuery<PromptDiffResponse>({
    queryKey: ['admin', 'prompts', promptId, 'diff', versionAId, versionBId] as const,
    queryFn: () =>
      apiClient.post<PromptDiffResponse>(`/admin/prompts/${promptId}/diff`, {
        versionA: versionAId,
        versionB: versionBId,
      }),
    enabled: open && Boolean(versionAId) && Boolean(versionBId),
    staleTime: 60_000,
  });

  const title = `Diff v${versionANumber ?? '?'} → v${versionBNumber ?? '?'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Mudanças entre as versões. A esquerda é a versão antiga (v{versionANumber ?? '?'}) e a
            direita a nova (v{versionBNumber ?? '?'}).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Visualização:</span>
          <div className="inline-flex overflow-hidden rounded border border-border">
            <button
              type="button"
              className={`px-2 py-1 text-xs ${
                mode === 'split' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              }`}
              onClick={() => setMode('split')}
            >
              Side-by-side
            </button>
            <button
              type="button"
              className={`px-2 py-1 text-xs ${
                mode === 'unified' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              }`}
              onClick={() => setMode('unified')}
            >
              Unificado
            </button>
          </div>
        </div>

        <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
          {isLoading && <p className="text-sm text-muted-foreground">Computando diff…</p>}
          {isError && (
            <div className="space-y-2">
              <p className="text-sm text-destructive">
                {error instanceof ApiError && error.status === 404
                  ? 'Versão não encontrada.'
                  : 'Falha ao computar diff.'}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          )}

          {data && (
            <>
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">System prompt</h3>
                <div className="space-y-2">
                  {data.sections.map((s) => (
                    <SectionItem key={s.name} section={s} mode={mode} />
                  ))}
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold">Few-shot examples</h3>
                <FewShotBlock diff={data.fewShots} />
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold">JSON schema</h3>
                <JsonSchemaBlock diff={data.jsonSchema} />
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PromptDiffViewer;
